import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import '../styles/KYBVerification.css';
import '../styles/Dashboard.css';
import { useAuth } from '../hooks';
import { companyService } from '../services';

const KYBVerification = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verifiedCompanies, setVerifiedCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://compliance-platform-func-dev.azurewebsites.net/api';

  const jurisdictions = [
    { code: 'FI', flag: '🇫🇮', name: 'Finland', placeholder: '1234567-8', label: 'Finnish Y-tunnus or Swedish Organisationsnummer', status: 'active' },
    { code: 'SE', flag: '🇸🇪', name: 'Sweden', placeholder: '556677-8899', label: 'Finnish Y-tunnus or Swedish Organisationsnummer', status: 'active' },
    { code: 'DK', flag: '🇩🇰', name: 'Denmark', placeholder: '12345678', label: 'CVR Number', status: 'active' },
    { code: 'EE', flag: '🇪🇪', name: 'Estonia', placeholder: '12345678', label: 'Registry Code (Registrikood)', status: 'soon' },
    { code: 'LV', flag: '🇱🇻', name: 'Latvia', placeholder: '12345678901', label: 'Registration Number (Reģ. nr.)', status: 'soon' },
    { code: 'LT', flag: '🇱🇹', name: 'Lithuania', placeholder: '123456789', label: 'Company Code (Įmonės kodas)', status: 'soon' }
  ];

  const selectedJurisdiction = jurisdictions.find(j => j.code === selectedCountry);

  // Load verified companies from backend on mount
  useEffect(() => {
    if (currentUser?.id) {
      loadVerifiedCompanies();
    }
  }, [currentUser]);

  const loadVerifiedCompanies = async () => {
    setLoadingCompanies(true);
    try {
      console.log('Fetching companies for user:', currentUser.id);
      const response = await fetch(`${apiUrl}/users/${currentUser.id}/companies`);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Received companies data:', data);
        
        if (data.companies && data.companies.length > 0) {
          // Transform backend data - backend uses snake_case
          const transformedCompanies = data.companies.map(company => ({
            id: company.id,
            name: company.name || 'Verified Company',
            businessId: company.business_id || company.businessId || company.registration_number,
            countryCode: company.country_code || company.countryCode,
            status: company.status || 'active',
            registrationDate: company.registration_date || company.registrationDate,
            website: company.website,
            address: company.address,
            industry: company.industry,
            verifiedAt: company.created_at || company.createdAt || company.verification_date
          }));
          // Format address objects to strings
          const formattedCompanies = transformedCompanies.map(company => {
            if (company.address && typeof company.address === 'object') {
              const addr = company.address;
              company.address = [addr.street, addr.houseNumber, addr.postalCode, addr.city, addr.country]
                .filter(Boolean)
                .join(', ');
            }
            return company;
          });
          
          console.log('Transformed companies:', formattedCompanies);
          setVerifiedCompanies(formattedCompanies);
        } else {
          console.log('No companies in response or empty array');
          setVerifiedCompanies([]);
        }
      } else {
        console.error('Failed to fetch companies, status:', response.status);
      }
    } catch (err) {
      console.error('Error loading verified companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setVerificationResult(null);

    try {
      const response = await fetch(`${apiUrl}/companies/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          countryCode: selectedCountry,
          companyId: companyId,
          userId: currentUser?.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      console.log('Verification result:', data);
      setVerificationResult(data);

      if (data.verified) {
        console.log('Verification successful, reloading companies...');
        // Reload companies list from backend
        await loadVerifiedCompanies();
      } else {
        setError(`✗ Company not found in ${selectedJurisdiction?.name} business register`);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        {/* Main Content */}
        <main className="main-content">
          <div className="content-area">
          {/* Hero Section - Bloomberg Terminal Style */}
          <div className="kyb-hero">
            <div className="kyb-hero-content">
              <div className="kyb-hero-label">COMPLIANCE VERIFICATION</div>
              <h1 className="kyb-hero-title">Know Your Business (eKYB) Verification</h1>
              <p className="kyb-hero-description">
                Enterprise-grade company verification across EU jurisdictions. Instant access to official business registers with comprehensive UBO screening, sanctions checks, and regulatory compliance for MiCA-licensed entities.
              </p>
              <div className="kyb-countries">
                {jurisdictions.map((jurisdiction) => (
                  <span
                    key={jurisdiction.code}
                    className={`kyb-country-badge ${jurisdiction.status}`}
                  >
                    {jurisdiction.flag} {jurisdiction.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Verification Wizard - Professional */}
          <div className="kyb-wizard">
            {/* Wizard Stepper */}
            <div className="wizard-stepper">
              <div className="wizard-step active">
                <div className="step-number">1</div>
                <div className="step-label">Select Jurisdiction</div>
              </div>
              <div className="wizard-step">
                <div className="step-number">2</div>
                <div className="step-label">Company Details</div>
              </div>
              <div className="wizard-step">
                <div className="step-number">3</div>
                <div className="step-label">Registry Check</div>
              </div>
              <div className="wizard-step">
                <div className="step-number">4</div>
                <div className="step-label">UBO Screening</div>
              </div>
              <div className="wizard-step">
                <div className="step-number">5</div>
                <div className="step-label">AML Check</div>
              </div>
              <div className="wizard-step">
                <div className="step-number">6</div>
                <div className="step-label">Report</div>
              </div>
            </div>

            {/* Country Selector - Interactive Cards */}
            <div className="kyb-form-section">
              <h2 className="kyb-section-title">Select Jurisdiction</h2>
              <div className="country-selector">
                {jurisdictions.map((jurisdiction) => (
                  <div
                    key={jurisdiction.code}
                    className={`country-card ${selectedCountry === jurisdiction.code ? 'selected' : ''} ${jurisdiction.status === 'soon' ? 'disabled' : ''}`}
                    onClick={() => jurisdiction.status === 'active' && setSelectedCountry(jurisdiction.code)}
                  >
                    <div className="country-flag">{jurisdiction.flag}</div>
                    <div className="country-name">{jurisdiction.name}</div>
                    <div className="country-code">{jurisdiction.code}</div>
                    {jurisdiction.status === 'soon' && (
                      <div className="country-status">COMING SOON</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Verification Form */}
            {selectedCountry && (
              <div className="kyb-form-section">
                <h2 className="kyb-section-title">Enter Company Registration Number</h2>
                <div className="kyb-form-grid">
                  <div className="kyb-form-group full-width">
                    <label className="kyb-form-label">Business Registration Number</label>
                    <input
                      type="text"
                      className="kyb-form-input"
                      placeholder={selectedJurisdiction?.placeholder || "e.g., 1234567-8"}
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value)}
                    />
                    <span className="kyb-form-hint">
                      {selectedJurisdiction?.label || 'Finnish Y-tunnus or Swedish Organisationsnummer'}
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="kyb-error">{error}</div>
                )}

                <div className="kyb-info-box">
                  <h3 className="kyb-info-title">What's included in the verification:</h3>
                  <ul className="kyb-info-list">
                    <li className="kyb-info-item">Company legal name and registration status</li>
                    <li className="kyb-info-item">Directors and board members</li>
                    <li className="kyb-info-item">Beneficial owners (UBO) greater than 25%</li>
                    <li className="kyb-info-item">Sanctions screening (EU, OFAC, UN)</li>
                    <li className="kyb-info-item">PEP (Politically Exposed Persons) check</li>
                    <li className="kyb-info-item">Comprehensive PDF report for NCA submission</li>
                  </ul>
                </div>

                <div className="kyb-actions">
                  <button
                    className="kyb-btn kyb-btn-secondary"
                    onClick={() => {
                      setSelectedCountry('');
                      setCompanyId('');
                      setError('');
                    }}
                  >
                    Reset
                  </button>
                  <button
                    className="kyb-btn kyb-btn-primary"
                    onClick={handleVerify}
                    disabled={loading || !selectedCountry || !companyId}
                  >
                    {loading ? 'Verifying...' : 'Verify Company'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Verification Result - Professional */}
          {verificationResult && verificationResult.verified && (
            <div className="verification-result">
              <div className="result-header">
                <div className="result-icon">✓</div>
                <div>
                  <h3 className="result-title">Company Verified Successfully</h3>
                  <p className="result-subtitle">All regulatory checks completed</p>
                </div>
              </div>
              <div className="result-details">
                {verificationResult.company?.name && (
                  <div className="result-field">
                    <div className="result-label">Company Name</div>
                    <div className="result-value">{verificationResult.company.name}</div>
                  </div>
                )}
                {verificationResult.company?.business_id && (
                  <div className="result-field">
                    <div className="result-label">Business ID</div>
                    <div className="result-value">{verificationResult.company.business_id}</div>
                  </div>
                )}
                {verificationResult.company?.country_code && (
                  <div className="result-field">
                    <div className="result-label">Jurisdiction</div>
                    <div className="result-value">{verificationResult.company.country_code}</div>
                  </div>
                )}
                {verificationResult.company?.status && (
                  <div className="result-field">
                    <div className="result-label">Status</div>
                    <div className="result-value" style={{textTransform: 'capitalize'}}>{verificationResult.company.status}</div>
                  </div>
                )}
                {verificationResult.company?.registration_date && (
                  <div className="result-field">
                    <div className="result-label">Registration Date</div>
                    <div className="result-value">{verificationResult.company.registration_date}</div>
                  </div>
                )}
                {verificationResult.company?.website && (
                  <div className="result-field">
                    <div className="result-label">Website</div>
                    <div className="result-value">{verificationResult.company.website}</div>
                  </div>
                )}
                {verificationResult.company?.industry && (
                  <div className="result-field">
                    <div className="result-label">Industry</div>
                    <div className="result-value">{verificationResult.company.industry}</div>
                  </div>
                )}
                {verificationResult.company?.address && (
                  <div className="result-field" style={{gridColumn: '1 / -1'}}>
                    <div className="result-label">Registered Address</div>
                    <div className="result-value">
                      {typeof verificationResult.company.address === 'object'
                        ? [
                            verificationResult.company.address.street,
                            verificationResult.company.address.houseNumber,
                            verificationResult.company.address.postalCode,
                            verificationResult.company.address.city,
                            verificationResult.company.address.country
                          ].filter(Boolean).join(', ')
                        : verificationResult.company.address
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enterprise Solutions */}
          <div className="pricing-card">
            <h3 className="pricing-title">Enterprise Verification Solutions</h3>
            <p className="pricing-description">
              Automate your compliance workflows with our API integration. Batch verification, real-time monitoring, and dedicated support for financial institutions and MiCA-licensed entities.
            </p>
            <button className="kyb-btn kyb-btn-secondary">
              Contact Enterprise Sales
            </button>
          </div>

          {/* Verified Companies - Professional Cards */}
          {verifiedCompanies.length > 0 && (
            <div className="verified-companies-section">
              <div className="companies-header">
                <h2 className="companies-title">Recently Verified Companies</h2>
                <span className="companies-count">{verifiedCompanies.length} verified</span>
              </div>

              <div className="companies-grid">
                {verifiedCompanies.map((company) => {
                  const jurisdiction = jurisdictions.find(j => j.code === company.countryCode);
                  return (
                    <div key={company.id} className="company-card">
                      <div className="company-header">
                        <div className="company-meta">
                          <span className="company-country">
                            {jurisdiction?.flag || '🏢'} {jurisdiction?.name || company.countryCode}
                          </span>
                          <span className="company-id">{company.businessId}</span>
                        </div>
                      </div>

                      <h3 className="company-name">{company.name}</h3>

                      <div className="company-details">
                        {company.status && `Status: ${company.status}`}
                        {company.registrationDate && ` • Registered: ${company.registrationDate}`}
                        {company.industry && ` • ${company.industry}`}
                      </div>

                      <div className="company-footer">
                        <span>
                          Verified: {company.verifiedAt ? new Date(company.verifiedAt).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'}) : 'Recently'}
                        </span>
                        {company.website && (
                          <>
                            <span>•</span>
                            <a href={company.website} target="_blank" rel="noopener noreferrer" className="company-link">
                              {company.website}
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
    </>
  );
};

export default KYBVerification;
